"use client";

import { c as _c } from "react/compiler-runtime";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getTimeAgoShort } from "@/helpers/Helpers";
import type { ImageScale } from "@/helpers/image.helpers";
import { areSameProfileIdentity } from "@/helpers/ProfileHelpers";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import useIsMobileDevice from "@/hooks/isMobileDevice";
import useIsTouchDevice from "@/hooks/useIsTouchDevice";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCallback, useState } from "react";
import DropCurationButton from "../DropCurationButton";
import DropMinimalIdentityRow from "../DropMinimalIdentityRow";
import WaveDropActions from "../WaveDropActions";
import WaveDropAuthorPfp from "../WaveDropAuthorPfp";
import WaveDropContent from "../WaveDropContent";
import WaveDropMetadata from "../WaveDropMetadata";
import WaveDropMobileMenu from "../WaveDropMobileMenu";
import WaveDropReactions from "../WaveDropReactions";
import type { DropIdentityMode, DropInteractionParams, DropTimestampLayout } from "../drop.types";
import { DropLocation, hasDropFooter } from "../drop.types";
import type { DropContentPresentation } from "../dropContentPresentation";
import ParticipationIdentityProfileCard from "./ParticipationIdentityProfileCard";
import { getParticipationIdentityProfile, getParticipationVisibleMetadata } from "./participationIdentityProfile.helpers";
interface EndedParticipationDropProps {
  readonly drop: ExtendedDrop;
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly location: DropLocation;
  readonly onReply: (param: DropInteractionParams) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly footer?: React.ReactNode;
  readonly identityMode?: DropIdentityMode | undefined;
  readonly timestampLayout?: DropTimestampLayout | undefined;
  readonly showInteractions?: boolean | undefined;
  readonly inlineAuthorOnDesktop?: boolean | undefined;
  readonly mediaImageScale?: ImageScale | undefined;
  readonly fullWidthMedia?: boolean | undefined;
  readonly fullWidthLinkPreviews?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly winningThresholdMinDurationMs?: number | null | undefined;
  readonly contentPresentation?: DropContentPresentation | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
export default function EndedParticipationDrop(t0) {
  const $ = _c(99);
  const {
    drop,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    location,
    onReply,
    onQuoteClick,
    onDropContentClick,
    footer,
    identityMode: t1,
    timestampLayout: t2,
    showInteractions: t3,
    inlineAuthorOnDesktop: t4,
    mediaImageScale,
    fullWidthMedia: t5,
    fullWidthLinkPreviews: t6,
    contentPresentation: t7,
    embedPath,
    quotePath,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const identityMode = t1 === undefined ? "default" : t1;
  const timestampLayout = t2 === undefined ? "inline" : t2;
  const showInteractions = t3 === undefined ? true : t3;
  const inlineAuthorOnDesktop = t4 === undefined ? false : t4;
  const fullWidthMedia = t5 === undefined ? false : t5;
  const fullWidthLinkPreviews = t6 === undefined ? false : t6;
  const contentPresentation = t7 === undefined ? "default" : t7;
  const isActiveDrop = activeDrop?.drop.id === drop.id;
  const router = useRouter();
  let identityProfile;
  let t8;
  let visibleMetadata;
  if ($[0] !== drop.author || $[1] !== drop.metadata || $[2] !== drop.wave) {
    identityProfile = getParticipationIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    let t9;
    if ($[6] !== drop.metadata || $[7] !== drop.wave) {
      t9 = getParticipationVisibleMetadata({
        wave: drop.wave,
        metadata: drop.metadata
      });
      $[6] = drop.metadata;
      $[7] = drop.wave;
      $[8] = t9;
    } else {
      t9 = $[8];
    }
    visibleMetadata = t9;
    t8 = identityProfile ? areSameProfileIdentity({
      left: drop.author,
      right: identityProfile
    }) : false;
    $[0] = drop.author;
    $[1] = drop.metadata;
    $[2] = drop.wave;
    $[3] = identityProfile;
    $[4] = t8;
    $[5] = visibleMetadata;
  } else {
    identityProfile = $[3];
    t8 = $[4];
    visibleMetadata = $[5];
  }
  const isSelfNominee = t8;
  const [activePartIndex, setActivePartIndex] = useState(0);
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const [isSlideUp, setIsSlideUp] = useState(false);
  const isMobile = useIsMobileDevice();
  const hasTouch = useIsTouchDevice() || isMobile;
  const showIdentity = identityMode !== "hidden";
  const isStackedTimestamp = timestampLayout === "stacked";
  const shouldOffsetRows = showIdentity && !inlineAuthorOnDesktop;
  let t9;
  if ($[9] !== router) {
    t9 = (e, path) => {
      e.preventDefault();
      e.stopPropagation();
      router.push(path);
    };
    $[9] = router;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const handleNavigation = t9;
  let t10;
  if ($[11] !== hasTouch || $[12] !== showInteractions) {
    t10 = () => {
      if (!showInteractions || !hasTouch) {
        return;
      }
      setLongPressTriggered(true);
      setIsSlideUp(true);
    };
    $[11] = hasTouch;
    $[12] = showInteractions;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const handleLongPress = t10;
  let t11;
  if ($[14] !== activePartIndex || $[15] !== drop || $[16] !== onReply) {
    t11 = () => {
      setIsSlideUp(false);
      onReply({
        drop,
        partId: drop.parts[activePartIndex]?.part_id
      });
    };
    $[14] = activePartIndex;
    $[15] = drop;
    $[16] = onReply;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const handleOnReply = t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      setIsSlideUp(false);
    };
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const handleOnAddReaction = t12;
  const getDropLocationBackground = _temp;
  const dropBackgroundClass = isActiveDrop ? "tw-bg-[#3CCB7F]/10" : getDropLocationBackground();
  let t13;
  if ($[19] !== drop || $[20] !== handleNavigation || $[21] !== identityMode || $[22] !== isStackedTimestamp || $[23] !== timestampLayout) {
    t13 = identityMode === "minimal" ? <DropMinimalIdentityRow drop={drop} timestampLayout={timestampLayout} /> : <div className="tw-flex tw-flex-col tw-gap-y-2"><div className={isStackedTimestamp ? "tw-flex tw-min-w-0 tw-flex-col tw-items-start tw-gap-y-1" : "tw-flex tw-items-center tw-gap-x-2"}><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} /><p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link onClick={e_0 => handleNavigation(e_0, `/${drop.author.handle ?? drop.author.primary_address}`)} href={`/${drop.author.handle ?? drop.author.primary_address}`} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{drop.author.handle ?? drop.author.primary_address}</Link></p>{!isStackedTimestamp && <div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-700" />}{!isStackedTimestamp && <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{getTimeAgoShort(drop.created_at)}</p>}</div>{isStackedTimestamp && <p className="tw-mb-0 tw-whitespace-nowrap tw-text-xs tw-font-normal tw-leading-none tw-text-iron-500">{getTimeAgoShort(drop.created_at)}</p>}</div><div className="tw-flex tw-w-fit tw-items-center tw-whitespace-nowrap tw-rounded-md tw-border tw-border-solid tw-border-iron-500/25 tw-bg-iron-600/10 tw-px-2 tw-py-0.5 tw-font-medium tw-text-iron-500"><span className="tw-text-xs">Participant</span></div></div>;
    $[19] = drop;
    $[20] = handleNavigation;
    $[21] = identityMode;
    $[22] = isStackedTimestamp;
    $[23] = timestampLayout;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const identityHeader = t13;
  let t14;
  if ($[25] !== activePartIndex || $[26] !== contentPresentation || $[27] !== drop || $[28] !== embedDepth || $[29] !== embedPath || $[30] !== fullWidthLinkPreviews || $[31] !== fullWidthMedia || $[32] !== handleLongPress || $[33] !== hasTouch || $[34] !== maxEmbedDepth || $[35] !== mediaImageScale || $[36] !== onDropContentClick || $[37] !== onQuoteClick || $[38] !== quotePath) {
    t14 = <WaveDropContent drop={drop} activePartIndex={activePartIndex} setActivePartIndex={setActivePartIndex} onLongPress={handleLongPress} onDropContentClick={onDropContentClick} onQuoteClick={onQuoteClick} setLongPressTriggered={setLongPressTriggered} isCompetitionDrop={true} mediaImageScale={mediaImageScale} mediaContainerHeightClassName="tw-h-96" fullWidthMedia={fullWidthMedia} fullWidthLinkPreviews={fullWidthLinkPreviews} hasTouch={hasTouch} contentPresentation={contentPresentation} embedPath={embedPath} quotePath={quotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} />;
    $[25] = activePartIndex;
    $[26] = contentPresentation;
    $[27] = drop;
    $[28] = embedDepth;
    $[29] = embedPath;
    $[30] = fullWidthLinkPreviews;
    $[31] = fullWidthMedia;
    $[32] = handleLongPress;
    $[33] = hasTouch;
    $[34] = maxEmbedDepth;
    $[35] = mediaImageScale;
    $[36] = onDropContentClick;
    $[37] = onQuoteClick;
    $[38] = quotePath;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  const content = t14;
  const t15 = `${location === DropLocation.WAVE ? "tw-px-4 tw-py-1" : ""} tw-w-full`;
  let t16;
  if ($[40] !== activePartIndex || $[41] !== drop || $[42] !== handleOnReply || $[43] !== isMobile || $[44] !== showInteractions || $[45] !== showReplyAndQuote) {
    t16 = !isMobile && showInteractions && showReplyAndQuote && <WaveDropActions drop={drop} activePartIndex={activePartIndex} showVoting={false} onReply={handleOnReply} />;
    $[40] = activePartIndex;
    $[41] = drop;
    $[42] = handleOnReply;
    $[43] = isMobile;
    $[44] = showInteractions;
    $[45] = showReplyAndQuote;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  const t17 = `tw-flex tw-w-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-px-4 tw-py-3 tw-transition-colors tw-duration-200 tw-ease-linear ${dropBackgroundClass}`;
  const t18 = `tw-flex tw-w-full tw-border-0 tw-bg-transparent tw-text-left ${inlineAuthorOnDesktop ? "tw-flex-col tw-gap-y-2" : "tw-gap-x-3"}`;
  let t19;
  if ($[47] !== drop || $[48] !== identityHeader || $[49] !== inlineAuthorOnDesktop || $[50] !== showIdentity) {
    t19 = inlineAuthorOnDesktop ? showIdentity && <div className="tw-flex tw-w-full tw-items-center tw-gap-x-2"><WaveDropAuthorPfp drop={drop} /><div className="tw-min-w-0 tw-flex-1">{identityHeader}</div></div> : showIdentity && <WaveDropAuthorPfp drop={drop} />;
    $[47] = drop;
    $[48] = identityHeader;
    $[49] = inlineAuthorOnDesktop;
    $[50] = showIdentity;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  const t20 = showIdentity && !inlineAuthorOnDesktop && identityHeader;
  let t21;
  if ($[52] !== drop.wave || $[53] !== handleNavigation || $[54] !== identityMode || $[55] !== showWaveInfo) {
    t21 = identityMode === "default" && showWaveInfo && (() => {
      const waveMeta = (drop.wave as unknown as {
        chat?: {
          scope?: {
            group?: {
              is_direct_message?: boolean | undefined;
            } | undefined;
          } | undefined;
        } | undefined;
      }).chat;
      const isDirectMessage = waveMeta?.scope?.group?.is_direct_message ?? false;
      const waveHref = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      return <Link href={waveHref} onClick={e_1 => handleNavigation(e_1, waveHref)} className="tw-leading-0 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    })();
    $[52] = drop.wave;
    $[53] = handleNavigation;
    $[54] = identityMode;
    $[55] = showWaveInfo;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== content || $[58] !== t20 || $[59] !== t21) {
    t22 = <div className="tw-flex tw-w-full tw-flex-col tw-gap-y-2">{t20}{t21}{content}</div>;
    $[57] = content;
    $[58] = t20;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== t18 || $[62] !== t19 || $[63] !== t22) {
    t23 = <div className={t18}>{t19}{t22}</div>;
    $[61] = t18;
    $[62] = t19;
    $[63] = t22;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== drop.id || $[66] !== identityProfile || $[67] !== isSelfNominee || $[68] !== shouldOffsetRows) {
    t24 = identityProfile && <div className={shouldOffsetRows ? "tw-ml-[3.25rem]" : ""}><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant="chat" showIdentityHeader={!isSelfNominee} /></div>;
    $[65] = drop.id;
    $[66] = identityProfile;
    $[67] = isSelfNominee;
    $[68] = shouldOffsetRows;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  let t25;
  if ($[70] !== visibleMetadata) {
    t25 = visibleMetadata.length > 0 && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><WaveDropMetadata metadata={visibleMetadata} /></div>;
    $[70] = visibleMetadata;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== drop || $[73] !== showInteractions) {
    t26 = showInteractions && <div className="tw-flex tw-w-full tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1"><DropCurationButton dropId={drop.id} waveId={drop.wave.id} isCuratable={drop.context_profile_context?.curatable ?? false} isCurated={drop.context_profile_context?.curated ?? false} /><WaveDropReactions drop={drop} /></div>;
    $[72] = drop;
    $[73] = showInteractions;
    $[74] = t26;
  } else {
    t26 = $[74];
  }
  let t27;
  if ($[75] !== footer || $[76] !== shouldOffsetRows) {
    t27 = hasDropFooter(footer) && <div className={`${shouldOffsetRows ? "tw-ml-[3.25rem]" : ""} tw-pb-1 tw-pt-2`}>{footer}</div>;
    $[75] = footer;
    $[76] = shouldOffsetRows;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== drop || $[79] !== handleOnReply || $[80] !== isSlideUp || $[81] !== longPressTriggered || $[82] !== showInteractions || $[83] !== showReplyAndQuote) {
    t28 = showInteractions && <WaveDropMobileMenu drop={drop} isOpen={isSlideUp} longPressTriggered={longPressTriggered} showReplyAndQuote={showReplyAndQuote} setOpen={setIsSlideUp} onReply={handleOnReply} onAddReaction={handleOnAddReaction} />;
    $[78] = drop;
    $[79] = handleOnReply;
    $[80] = isSlideUp;
    $[81] = longPressTriggered;
    $[82] = showInteractions;
    $[83] = showReplyAndQuote;
    $[84] = t28;
  } else {
    t28 = $[84];
  }
  let t29;
  if ($[85] !== t17 || $[86] !== t23 || $[87] !== t24 || $[88] !== t25 || $[89] !== t26 || $[90] !== t27 || $[91] !== t28) {
    t29 = <div className={t17}>{t23}{t24}{t25}{t26}{t27}{t28}</div>;
    $[85] = t17;
    $[86] = t23;
    $[87] = t24;
    $[88] = t25;
    $[89] = t26;
    $[90] = t27;
    $[91] = t28;
    $[92] = t29;
  } else {
    t29 = $[92];
  }
  let t30;
  if ($[93] !== t16 || $[94] !== t29) {
    t30 = <div className="tw-group tw-relative tw-w-full">{t16}{t29}</div>;
    $[93] = t16;
    $[94] = t29;
    $[95] = t30;
  } else {
    t30 = $[95];
  }
  let t31;
  if ($[96] !== t15 || $[97] !== t30) {
    t31 = <div className={t15}>{t30}</div>;
    $[96] = t15;
    $[97] = t30;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  return t31;
}
function _temp() {
  return "tw-bg-iron-950 tw-ring-1 tw-ring-inset tw-ring-iron-800";
}